import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import { Box, Divider, Popover, Typography, Tooltip } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { DateRange } from "@mui/icons-material";
import { VARIANT_ENUM as BUTTON_VARIANT_ENUM } from "@kargo/shared-components.krg-button";
import KrgCheckbox from "@kargo/shared-components.krg-checkbox";
import usePlaylists from "../../hooks/usePlaylists";
import SearchInput from "../../components/SearchInput";
import useSnackbar from "../../hooks/useSnackbar";
import LoadingCircle from "../../components/LoadingCircle";
import TrackedKrgButton from "../../components/TrackedButton/TrackedKrgButton";
import { PlaylistCreationEnum } from "../../@types/playlist.types";

const useStyles = makeStyles({
  paper: {
    display: "flex",
    flexDirection: "column",
    padding: 20,
    borderRadius: 10,
    width: 280,
  },
  button: {
    alignSelf: "end",
    marginTop: 17,
  },
  checkbox: {
    marginLeft: 11,
    "& .MuiCheckbox-root:not(.Mui-checked)": { color: "#91A0AD" },
  },
});

type Props = {
  asAdmin?: boolean;
  anchorEl: any;
  publisherSlug?: string;
  videos: string[];
  onClose: () => void;
  onSubmit: (playlists: string[]) => void;
};

export default function AddToPlaylist({
  videos,
  onClose,
  onSubmit,
  anchorEl,
  asAdmin,
  publisherSlug,
}: Props) {
  const classes = useStyles();
  const { snackbarSuccess, snackbarError } = useSnackbar();

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const [selectedPlaylists, setSelectedPlaylists] = useState<string[]>([]);

  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timeout);
  }, [search]);

  const query = {
    ...(asAdmin && { asAdmin, slug: publisherSlug }),
    type: [PlaylistCreationEnum.CUSTOM],
    ...(debouncedSearch && { title: debouncedSearch }),
  };

  const {
    playlists,
    api,
    query: { isFetching },
  } = usePlaylists(query);

  const handleCheckboxToggle = (token: string, checked: boolean) => {
    const updated = checked
      ? [...selectedPlaylists, token]
      : selectedPlaylists.filter((t) => t !== token);
    setSelectedPlaylists(updated);
  };

  const handleSave = () => {
    const requests = selectedPlaylists.map((token) => {
      const target = playlists.find((p) => p.token === token);
      const updatedMedias = target?.medias.concat(videos) ?? videos;

      return new Promise((resolve) => {
        api.update(
          {
            token,
            data: { medias: updatedMedias },
          },
          { onSuccess: resolve },
        );
      });
    });

    return Promise.all(requests)
      .then(() => {
        const videoText = videos.length > 1 ? "Videos" : "Video";
        const playlistText =
          selectedPlaylists.length > 1 ? "Playlists" : "Playlist";
        snackbarSuccess(`${videoText} Added to ${playlistText}`);
        onSubmit(selectedPlaylists);
        setSelectedPlaylists([]);
        onClose();
      })
      .catch((err) => {
        console.error(err);
        snackbarError(
          "Error while updating playlists. Check the console for more information.",
        );
      });
  };

  const handleClose = () => {
    setSelectedPlaylists([]);
    onClose();
  };

  return (
    <Popover
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: -5, horizontal: "center" }}
      anchorEl={anchorEl}
      open={!!anchorEl}
      onClose={handleClose}
      classes={{ paper: classes.paper }}
    >
      <Typography
        sx={{ marginBottom: "21px", fontWeigth: "500", fontSize: "18px" }}
      >
        Add to Playlist
      </Typography>

      <SearchInput
        placeholder="Search"
        text={search}
        onTextChange={setSearch}
        isFullWidth
      />

      {isFetching && (
        <LoadingCircle
          style={{
            width: "60px",
            margin: "20px 20px 3px 20px",
            alignSelf: "center",
          }}
        />
      )}

      {playlists?.length > 0 && (
        <>
          <Divider sx={{ borderColor: "#91A0AD" }} />
          {playlists.map((playlist) => {
            const title = playlist.title || playlist.key || playlist.token;
            const shouldTruncate = title.length > 20;
            const displayText = shouldTruncate
              ? `${title.slice(0, 16)}...`
              : title;

            return (
              <Box
                display="flex"
                key={playlist.token}
                sx={{
                  height: "40px",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                {shouldTruncate ? (
                  <Tooltip title={title}>
                    <Box sx={{ fontSize: 11 }}>{displayText}</Box>
                  </Tooltip>
                ) : (
                  <Box sx={{ fontSize: 11 }}>{displayText}</Box>
                )}
                <Box
                  display="flex"
                  sx={{ color: "#91A0AD", fontSize: 10, alignItems: "center" }}
                >
                  <DateRange sx={{ fontSize: 10, marginRight: 1 }} />
                  {dayjs(playlist.creation_date).format("MM-DD-YY")}
                  <KrgCheckbox
                    className={classes.checkbox}
                    theme={KrgCheckbox.THEME_ENUM.v2}
                    isChecked={selectedPlaylists.includes(playlist.token)}
                    onToggle={(v) => handleCheckboxToggle(playlist.token, v)}
                  />
                </Box>
              </Box>
            );
          })}
        </>
      )}
      <TrackedKrgButton
        style={{ paddingTop: 10, paddingBottom: 10 }}
        id="confirm-add-to-playlist"
        variant={BUTTON_VARIANT_ENUM.contained}
        className={classes.button}
        text="Save"
        isEnabled={videos.length > 0 && selectedPlaylists.length > 0}
        onClick={handleSave}
      />
    </Popover>
  );
}
